// FILE: src/components/FlowBuilder/properties/DelayProperties.jsx
import React, { useState, useEffect } from 'react';
import {
    VStack,
    FormControl,
    FormLabel,
    Input,
    Button,
    Text,
    Box,
    Divider,
    HStack,
    RadioGroup,
    Radio,
    Stack,
    Textarea,
    Select as ChakraSelect,
    useToast,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Alert,
    AlertIcon,
    AlertDescription,
    Spinner,
} from '@chakra-ui/react';
import { LuZap, LuClock } from 'react-icons/lu';
import API from '../../../helpers/axiosInstance';
import { getApiHeaders } from '../../../utils/DefaultFunction';

export default function DelayProperties({
    nodeConfig,
    setNodeConfig,
    updateNodeData,
    setSelectedNode,
    nodes
}) {
    const toast = useToast();
    
    // State for attributes
    const [eventAttributes, setEventAttributes] = useState([]);
    const [loadingAttributes, setLoadingAttributes] = useState(false);
    
    // Determine which tab should be active based on node type/config
    const [tabIndex, setTabIndex] = useState(0);

    // Get event from start node
    const startNode = nodes?.find(n => n.type === 'startNode');
    const eventFromStart = startNode?.data?.eventName || '';

    // Initialize tab index based on node configuration
    useEffect(() => {
        if (nodeConfig.delaySubtype === 'dynamic') {
            setTabIndex(1); // Dynamic tab
        } else {
            setTabIndex(0); // Static tab
        }
    }, [nodeConfig.delaySubtype]);

    // Fetch attributes when component mounts or event changes
    useEffect(() => {
        if (eventFromStart) {
            fetchEventAttributes(eventFromStart);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventFromStart]);

    const fetchEventAttributes = async (eventName) => {
        if (!eventName) return;

        setLoadingAttributes(true);
        try {
            const response = await API.post(
                `${process.env.REACT_APP_API_URL}/fetch-unique-attributes`,
                { event: eventName },
                { headers: getApiHeaders() }
            );

            if (response.data.status) {
                setEventAttributes(response.data.data.attributes || []);
            } else {
                toast({
                    title: "Error",
                    description: response.data.message || "Failed to fetch event attributes",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error fetching event attributes:', error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch event attributes";
            toast({
                title: "Error",
                description: errorMessage,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingAttributes(false);
        }
    };

    // Filter API nodes from the flow
    const apiNodes = nodes?.filter(n => n.type === 'apiNode') || [];
    

    
    const handleTabChange = (index) => {
        setTabIndex(index);
        // Update the delay subtype based on selected tab
        if (index === 0) {
            setNodeConfig({ ...nodeConfig, delaySubtype: 'time' });
        } else {
            setNodeConfig({ ...nodeConfig, delaySubtype: 'dynamic' });
        }
    };

    const handleUpdateNode = () => {
        updateNodeData();
        setNodeConfig({ ...nodeConfig, event_field_type: eventFromStart })
        // Don't close the panel - just update the node
        // toast({
        //     title: "Node Updated",
        //     description: "Changes have been saved",
        //     status: "success",
        //     duration: 2000,
        //     isClosable: true,
        // });
    };

    return (
        <VStack spacing={4} align="stretch">
            <FormControl>
                <FormLabel fontSize="sm">Step Name</FormLabel>
                <Input
                    size="sm"
                    value={nodeConfig.label || ''}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
                    placeholder="e.g., Wait 1 Day"
                />
            </FormControl>

            <Divider />

            {/* Tabs for Static vs Dynamic Delay */}
            <Tabs 
                index={tabIndex} 
                onChange={handleTabChange}
                colorScheme="purple"
                variant="enclosed"
            >
                <TabList mb="1em">
                    <Tab>
                        <HStack spacing={1}>
                            <LuClock size={16} />
                            <Text>Static Delay</Text>
                        </HStack>
                    </Tab>
                    <Tab>
                        <HStack spacing={1}>
                            <LuZap size={16} />
                            <Text>Dynamic Delay</Text>
                        </HStack>
                    </Tab>
                </TabList>

                <TabPanels>
                    {/* ==================== STATIC DELAY TAB ==================== */}
                    <TabPanel p={0}>
                        <VStack spacing={4} align="stretch">
                            <FormControl>
                                <FormLabel fontSize="sm">Duration</FormLabel>
                                <HStack>
                                    <Input
                                        size="sm"
                                        type="number"
                                        
                                        value={nodeConfig.duration}
                                        onChange={(e) => setNodeConfig({ ...nodeConfig, duration: e.target.value })}
                                        placeholder="1"
                                    />
                                   
                                    <ChakraSelect
                                        size="sm"
                                        value={nodeConfig.unit || 'days'}
                                        onChange={(e) => setNodeConfig({ ...nodeConfig, unit: e.target.value })}
                                    >
                                        <option value="minutes">Minutes</option>
                                        <option value="hours">Hours</option>
                                        <option value="days">Days</option>
                                        <option value="weeks">Weeks</option>
                                    </ChakraSelect>
                                </HStack>
                                <Text fontSize="xs" color="gray.600" mt={1}>
                                    Fixed wait time before proceeding to the next step
                                </Text>
                            </FormControl>

                            <Alert status="info" borderRadius="md">
                                <AlertIcon />
                                <AlertDescription fontSize="xs">
                                    Use this for fixed delays that don't depend on external data
                                </AlertDescription>
                            </Alert>
                        </VStack>
                    </TabPanel>

                    {/* ==================== DYNAMIC DELAY TAB ==================== */}
                    <TabPanel p={0}>
                        <VStack spacing={4} align="stretch">
                            {/* Wait Time Source Selection */}
                            <FormControl>
                                <FormLabel fontSize="sm" display="flex" alignItems="center" gap={2}>
                                    <LuZap size={16} color="#FBAF3F" />
                                    Wait Time Source
                                </FormLabel>
                                <RadioGroup
                                    value={nodeConfig.dynamicWaitSource || 'event'}
                                    onChange={(value) => setNodeConfig({ ...nodeConfig, dynamicWaitSource: value })}
                                >
                                    <Stack spacing={2}>
                                        <Radio value="event" size="sm">From Event Data</Radio>
                                        {/* <Radio value="api" size="sm">From API Response</Radio>
                                        <Radio value="expression" size="sm">From Variable / Expression</Radio> */}
                                    </Stack>
                                </RadioGroup>
                            </FormControl>

                            <Divider />

                            {/* Configuration based on selected source */}
                            {nodeConfig.dynamicWaitSource === 'event' && (
                                <VStack spacing={3} align="stretch">
                                    {/* Display Event from Start Node */}
                                    <Box p={2} bg="blue.50" border="1px" borderColor="blue.200" borderRadius="md">
                                        <HStack spacing={2} >
                                            <Text fontSize="xs" fontWeight="semibold" color="blue.900">
                                                Event Source:
                                            </Text>
                                            <Text fontSize="xs" fontFamily="mono" color="blue.700">
                                                {eventFromStart || 'No event configured in Start node'}
                                            </Text>
                                        </HStack>
                                        {!eventFromStart && (
                                            <Alert status="warning" mt={2}>
                                                <AlertIcon />
                                                <AlertDescription fontSize="xs">
                                                    Please configure the event in the Start node first
                                                </AlertDescription>
                                            </Alert>
                                        )}
                                    </Box>

                                    {/* Field Type - Now populated from event attributes */}
                                    <FormControl>
                                        <FormLabel fontSize="xs">Field Type (Attribute)</FormLabel>
                                        <ChakraSelect
                                            size="sm"
                                            value={nodeConfig.dynamicEventFieldPath || ''}
                                            onChange={(e) => setNodeConfig({ ...nodeConfig, dynamicEventFieldPath: e.target.value })}
                                            placeholder={loadingAttributes ? "Loading attributes..." : "Select attribute"}
                                            isDisabled={loadingAttributes || !eventFromStart}
                                        >
                                            {eventAttributes.map((attr) => (
                                                <option key={attr.name} value={attr.name}>
                                                    {attr.name} ({attr.type})
                                                </option>
                                            ))}
                                        </ChakraSelect>
                                        {loadingAttributes && (
                                            <HStack mt={2} spacing={2}>
                                                <Spinner size="xs" />
                                                <Text fontSize="xs" color="gray.600">Loading attributes...</Text>
                                            </HStack>
                                        )}
                                        {!eventFromStart && (
                                            <Text fontSize="xs" color="orange.600" mt={1}>
                                                Configure event in Start node to see attributes
                                            </Text>
                                        )}
                                        {eventFromStart && eventAttributes.length === 0 && !loadingAttributes && (
                                            <Text fontSize="xs" color="gray.600" mt={1}>
                                                No attributes found for this event
                                            </Text>
                                        )}
                                    </FormControl>

                                    {/* <FormControl>
                                        <FormLabel fontSize="xs">Event Field Path</FormLabel>
                                        <Input
                                            size="sm"
                                            value={nodeConfig.dynamicEventFieldPath || ''}
                                            onChange={(e) => setNodeConfig({ ...nodeConfig, dynamicEventFieldPath: e.target.value })}
                                            placeholder="event.delivery.resume_at"
                                            fontFamily="mono"
                                            fontSize="xs"
                                        />
                                        <Text fontSize="xs" color="gray.600" mt={1}>
                                            Full path to the field in the event data
                                        </Text>
                                    </FormControl> */}

                                    <Alert status="info" borderRadius="md">
                                        <AlertIcon />
                                        <AlertDescription fontSize="xs">
                                            The wait time will be read from the {eventFromStart || 'event'} data at the specified path
                                        </AlertDescription>
                                    </Alert>
                                </VStack>
                            )}

                            {nodeConfig.dynamicWaitSource === 'api' && (
                                <VStack spacing={3} align="stretch">
                                    <FormControl>
                                        <FormLabel fontSize="xs">Source API Node</FormLabel>
                                        <ChakraSelect
                                            size="sm"
                                            value={nodeConfig.dynamicApiNode || ''}
                                            onChange={(e) => setNodeConfig({ ...nodeConfig, dynamicApiNode: e.target.value })}
                                        >
                                            <option value="">Select API Call node</option>
                                            {apiNodes.map(node => (
                                                <option key={node.id} value={node.data.label}>
                                                    {node.data.label}
                                                </option>
                                            ))}
                                        </ChakraSelect>
                                        {apiNodes.length === 0 && (
                                            <Alert status="warning" borderRadius="md">
                                                <AlertIcon />
                                                <AlertDescription fontSize="xs">
                                                    Add an API Call node to the flow first
                                                </AlertDescription>
                                            </Alert>
                                        )}
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel fontSize="xs">Response Field</FormLabel>
                                        <ChakraSelect
                                            size="sm"
                                            value={nodeConfig.dynamicApiField || 'resume_at'}
                                            onChange={(e) => setNodeConfig({ ...nodeConfig, dynamicApiField: e.target.value })}
                                        >
                                            <option value="resume_at">resume_at</option>
                                            <option value="wait_seconds">wait_seconds</option>
                                        </ChakraSelect>
                                        <Text fontSize="xs" color="gray.600" mt={1}>
                                            Uses wait time returned by an earlier API call
                                        </Text>
                                    </FormControl>

                                    <Alert status="info" borderRadius="md">
                                        <AlertIcon />
                                        <AlertDescription fontSize="xs">
                                            The wait time will be extracted from the API response
                                        </AlertDescription>
                                    </Alert>
                                </VStack>
                            )}

                            {nodeConfig.dynamicWaitSource === 'expression' && (
                                <FormControl>
                                    <FormLabel fontSize="xs">Expression Input</FormLabel>
                                    <Textarea
                                        size="sm"
                                        value={nodeConfig.dynamicExpression || ''}
                                        onChange={(e) => setNodeConfig({ ...nodeConfig, dynamicExpression: e.target.value })}
                                        placeholder="{{ event.wait_seconds || api.wait_seconds || 86400 }}"
                                        fontFamily="mono"
                                        fontSize="xs"
                                        minH="80px"
                                    />
                                    <Text fontSize="xs" color="gray.600" mt={1}>
                                        Advanced users can compute wait time using variables
                                    </Text>

                                    <Alert status="info" borderRadius="md" mt={2}>
                                        <AlertIcon />
                                        <AlertDescription fontSize="xs">
                                            Available: event.*, api.*, and standard operators
                                        </AlertDescription>
                                    </Alert>
                                </FormControl>
                            )}

                            <Divider />

                            {/* Fallback & Safety Settings */}
                            {/* <Box p={3} bg="orange.50" border="1px" borderColor="orange.200" borderRadius="md">
                                <HStack spacing={2} mb={2}>
                                    <LuAlertCircle size={16} color="#DD6B20" />
                                    <Text fontSize="xs" fontWeight="semibold" color="orange.900">
                                        Fallback & Safety
                                    </Text>
                                </HStack>

                                <VStack spacing={3} align="stretch">
                                    <FormControl>
                                        <FormLabel fontSize="xs">Fallback Wait Time</FormLabel>
                                        <HStack>
                                            <Input
                                                size="sm"
                                                type="number"
                                                min="1"
                                                value={nodeConfig.fallbackDuration || '1'}
                                                onChange={(e) => setNodeConfig({ ...nodeConfig, fallbackDuration: e.target.value })}
                                                placeholder="1"
                                            />
                                            <ChakraSelect
                                                size="sm"
                                                value={nodeConfig.fallbackUnit || 'days'}
                                                onChange={(e) => setNodeConfig({ ...nodeConfig, fallbackUnit: e.target.value })}
                                                maxW="140px"
                                            >
                                                <option value="minutes">Minutes</option>
                                                <option value="hours">Hours</option>
                                                <option value="days">Days</option>
                                                <option value="weeks">Weeks</option>
                                            </ChakraSelect>
                                        </HStack>
                                        <Text fontSize="xs" color="gray.600" mt={1}>
                                            Used if dynamic value is missing or invalid
                                        </Text>
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel fontSize="xs">Max Allowed Wait (Optional)</FormLabel>
                                        <HStack>
                                            <Input
                                                size="sm"
                                                type="number"
                                                min="1"
                                                value={nodeConfig.maxWaitDuration || '30'}
                                                onChange={(e) => setNodeConfig({ ...nodeConfig, maxWaitDuration: e.target.value })}
                                                placeholder="30"
                                            />
                                            <ChakraSelect
                                                size="sm"
                                                value={nodeConfig.maxWaitUnit || 'days'}
                                                onChange={(e) => setNodeConfig({ ...nodeConfig, maxWaitUnit: e.target.value })}
                                                maxW="140px"
                                            >
                                                <option value="hours">Hours</option>
                                                <option value="days">Days</option>
                                                <option value="weeks">Weeks</option>
                                                <option value="months">Months</option>
                                            </ChakraSelect>
                                        </HStack>
                                        <Text fontSize="xs" color="gray.600" mt={1}>
                                            Ensures flow continuity if dynamic data is unavailable
                                        </Text>
                                    </FormControl>
                                </VStack>
                            </Box> */}
                        </VStack>
                    </TabPanel>
                </TabPanels>
            </Tabs>

            <Divider />

            {/* Action Buttons */}
            <HStack>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedNode && setSelectedNode(null)}
                >
                    Close
                </Button>
                <Button
                    size="sm"
                    colorScheme="purple"
                    onClick={handleUpdateNode}
                    flex={1}
                >
                    Update Node
                </Button>
            </HStack>
        </VStack>
    );
}